import { Copy, Download, LogOut } from 'lucide-react';
import QRCode from 'qrcode';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button, Dialog, Field, Input, useToast } from '@/components/ui';
import { authApi } from '@/features/auth/auth-api';
import { useMe, useSession } from '@/features/auth/session-context';
import type { MfaSetup } from '@/features/auth/types';
import { SignedInDevicesDrawer } from '@/features/staff-sessions/SignedInDevicesDrawer';
import { isApiError, type ApiError } from '@/lib/api';
import { copyRecoveryCodes, downloadRecoveryCodes } from './recovery-codes';
import styles from './Shell.module.css';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

/** Sidebar footer: who is signed in, their shell settings and sign-out. */
export function UserMenu() {
  const { t } = useTranslation();
  const me = useMe();
  const { signOut, updateMe, selectRole } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  // RL-1784: an owner-doctor with two roles switches without signing out.
  const otherRoles = me.roles.filter((r) => r.key !== me.activeRole.key);

  async function switchRole(key: (typeof me.roles)[number]['key']) {
    setMenu(false);
    try {
      const next = await selectRole(key);
      // Another role sees other data: nothing cached is kept.
      queryClient.clear();
      void navigate('/');
      toast.show({
        tone: 'success',
        title: `Now working as ${next.activeRole?.name ?? key}`,
      });
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Could not switch role',
        body: isApiError(err) ? err.message : undefined,
        error: err,
      });
    }
  }
  const [dialog, setDialog] = useState<'pin' | 'mfa' | 'sessions' | null>(null);

  async function toggleLargerText() {
    setMenu(false);
    try {
      updateMe(await authApi.preferences({ largerText: !me.largerText }));
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Not saved',
        body: isApiError(err) ? err.message : undefined,
        error: err,
      });
    }
  }

  return (
    <div className={styles.user}>
      <button
        type="button"
        className={styles.userButton}
        aria-haspopup="menu"
        aria-expanded={menu}
        onClick={() => setMenu((m) => !m)}
      >
        <span className={styles.avatar} aria-hidden="true">
          {initials(me.fullName)}
        </span>
        <span className={styles.userText}>
          <span className={styles.userName}>{me.fullName}</span>
          <span className={styles.userRole}>{me.activeRole.name}</span>
        </span>
      </button>
      <button
        type="button"
        className={styles.signOut}
        onClick={() => void signOut()}
        aria-label={t('auth.signOut')}
        title={t('auth.signOut')}
      >
        <LogOut size={16} />
      </button>
      {menu && (
        <div
          className={styles.userMenu}
          role="menu"
          aria-label="Your settings"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setMenu(false);
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenu(false);
              setDialog('pin');
            }}
          >
            {me.pinSet ? 'Change unlock PIN' : 'Set unlock PIN'}
            {!me.pinSet && (
              <span className={styles.menuHint}>
                Needed to unlock the screen
              </span>
            )}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenu(false);
              setDialog('mfa');
            }}
          >
            Two-factor · {me.mfa.enabled ? 'on' : 'off'}
            {me.mfa.required && (
              <span className={styles.menuHint}>Required for your role</span>
            )}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenu(false);
              setDialog('sessions');
            }}
          >
            {t('staffSessions.signedInDevices')}
            <span className={styles.menuHint}>
              {t('staffSessions.signedInDevicesHint')}
            </span>
          </button>
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={me.largerText}
            onClick={() => void toggleLargerText()}
          >
            Larger text · {me.largerText ? 'on' : 'off'}
          </button>
          {otherRoles.map((r) => (
            <button
              key={r.key}
              type="button"
              role="menuitem"
              onClick={() => void switchRole(r.key)}
            >
              Switch role · {r.name}
              <span className={styles.menuHint}>
                You are working as {me.activeRole.name}
              </span>
            </button>
          ))}
        </div>
      )}
      {dialog === 'pin' && <PinDialog onClose={() => setDialog(null)} />}
      {dialog === 'mfa' && <TwoFactorDialog onClose={() => setDialog(null)} />}
      {dialog === 'sessions' && (
        <SignedInDevicesDrawer onClose={() => setDialog(null)} />
      )}
    </div>
  );
}

function PinDialog({ onClose }: { onClose: () => void }) {
  const { updateMe } = useSession();
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const valid = /^\d{4,8}$/.test(pin) && pin === confirm && password.length > 0;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      updateMe(await authApi.setPin(password, pin));
      toast.show({ tone: 'success', title: 'Unlock PIN saved' });
      onClose();
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Not saved');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      variant="modal"
      title="Unlock PIN"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={busy}
            onClick={() => void save()}
          >
            Save PIN
          </Button>
        </>
      }
    >
      <div className={styles.dialogBody}>
        <p className={styles.searchNote}>
          The screen locks after a while without use and patient details are
          hidden. This PIN unlocks it.
        </p>
        <Field label="Current password">
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="New PIN (4 to 8 digits)">
          <Input
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
        <Field
          label="Repeat PIN"
          error={
            confirm && confirm !== pin ? 'The PINs do not match' : undefined
          }
        >
          <Input
            type="password"
            inputMode="numeric"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
        {error && (
          <p className={styles.lockError} role="alert">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/** Scannable QR for an otpauth:// URI, plus the secret for typing in. */
export function TotpQr({ setup }: { setup: MfaSetup }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(setup.otpauthUri, { margin: 1, width: 184 })
      .then((url) => live && setSrc(url))
      .catch(() => live && setSrc(null));
    return () => {
      live = false;
    };
  }, [setup.otpauthUri]);
  return (
    <div className={styles.qr}>
      {src && (
        <img
          src={src}
          alt="QR code for your authenticator app"
          width={184}
          height={184}
        />
      )}
      <p className={styles.searchNote}>
        Or type this key:{' '}
        <code className={styles.secret} data-testid="totp-secret">
          {setup.secret}
        </code>
      </p>
    </div>
  );
}

export function RecoveryCodes({ codes }: { codes: string[] }) {
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
  return (
    <div className={styles.dialogBody}>
      <p className={styles.searchNote}>
        Keep these recovery codes somewhere safe. Each one signs you in once if
        you lose your phone. They are shown only now.
      </p>
      <ul className={styles.codes} aria-label="Recovery codes">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ul>
      <div className={styles.codeActions}>
        <Button
          size="sm"
          icon={<Copy size={15} />}
          onClick={() =>
            void copyRecoveryCodes(codes).then((ok) =>
              setCopy(ok ? 'copied' : 'failed'),
            )
          }
        >
          {copy === 'copied' ? 'Copied' : 'Copy codes'}
        </Button>
        <Button
          size="sm"
          icon={<Download size={15} />}
          onClick={() => downloadRecoveryCodes(codes)}
        >
          Download (.txt)
        </Button>
      </div>
      {/* Announces the outcome only, never the codes. */}
      <p className={styles.searchNote} role="status">
        {copy === 'copied'
          ? 'Recovery codes copied to the clipboard.'
          : copy === 'failed'
            ? 'Could not copy. Select the codes and copy them, or download them.'
            : ''}
      </p>
    </div>
  );
}

function recoveryCodeError(error: ApiError): string {
  switch (error.code) {
    case 'INVALID_MFA_CODE':
      return 'twoFactor.invalidCode';
    case 'MFA_NOT_ENABLED':
      return 'twoFactor.notEnabled';
    case 'RATE_LIMITED':
      return 'twoFactor.rateLimited';
    default:
      return 'twoFactor.genericError';
  }
}

function TwoFactorDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const { reloadMe, updateMe } = useSession();
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  let body;
  let footer;
  if (codes) {
    body = (
      <>
        <RecoveryCodes codes={codes} />
        <p className={styles.searchNote}>
          {t('twoFactor.oneTimeAcknowledgement')}
        </p>
      </>
    );
    footer = (
      <Button variant="primary" onClick={onClose}>
        {t('twoFactor.savedCodes')}
      </Button>
    );
  } else if (me.mfa.enabled) {
    body = (
      <div className={styles.dialogBody}>
        <p>
          Two-factor is on. Each sign-in asks for the code from your
          authenticator app.
        </p>
        {me.mfa.required ? (
          <p className={styles.searchNote}>
            It is required for your role and cannot be turned off.
          </p>
        ) : null}
        <Field
          label={
            me.mfa.required
              ? t('twoFactor.appCodeForNewCodes')
              : 'Code from your app'
          }
          hint={t('twoFactor.newCodesHint')}
        >
          <Input
            inputMode="numeric"
            maxLength={6}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
        {!me.mfa.required && (
          <p className={styles.searchNote}>
            Enter the code above to turn two-factor off or to replace your
            recovery codes.
          </p>
        )}
        {error !== null && (
          <p className={styles.lockError} role="alert">
            {isApiError(error)
              ? t(recoveryCodeError(error))
              : t('twoFactor.genericError')}
            {isApiError(error) && error.requestId
              ? ` ${t('common.requestId', { id: error.requestId })}`
              : ''}
          </p>
        )}
      </div>
    );
    footer = (
      <>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="primary"
          disabled={code.length !== 6}
          loading={busy}
          onClick={() =>
            void run(async () => {
              const result = await authApi.mfaRecoveryCodes(code);
              setCode('');
              setCodes(result.recoveryCodes);
            })
          }
        >
          {t('twoFactor.getNewCodes')}
        </Button>
        {!me.mfa.required && (
          <Button
            variant="danger"
            disabled={code.length !== 6}
            loading={busy}
            onClick={() =>
              void run(async () => {
                updateMe(await authApi.mfaDisable(code));
                onClose();
              })
            }
          >
            Turn off
          </Button>
        )}
      </>
    );
  } else if (setup) {
    body = (
      <div className={styles.dialogBody}>
        <p>
          Scan this with an authenticator app, then enter the 6-digit code it
          shows.
        </p>
        <TotpQr setup={setup} />
        <Field label="Code from your app">
          <Input
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
        {error !== null && (
          <p className={styles.lockError} role="alert">
            {isApiError(error) ? error.message : 'Not done'}
            {isApiError(error) && error.requestId
              ? ` ${t('common.requestId', { id: error.requestId })}`
              : ''}
          </p>
        )}
      </div>
    );
    footer = (
      <>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          disabled={code.length !== 6}
          loading={busy}
          onClick={() =>
            void run(async () => {
              setCodes((await authApi.mfaEnable(code)).recoveryCodes);
              await reloadMe();
            })
          }
        >
          Turn on
        </Button>
      </>
    );
  } else {
    body = (
      <div className={styles.dialogBody}>
        <p>Two-factor adds a code from your phone to every sign-in.</p>
        {error !== null && (
          <p className={styles.lockError} role="alert">
            {isApiError(error) ? error.message : 'Not done'}
            {isApiError(error) && error.requestId
              ? ` ${t('common.requestId', { id: error.requestId })}`
              : ''}
          </p>
        )}
      </div>
    );
    footer = (
      <>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="primary"
          loading={busy}
          onClick={() =>
            void run(async () => setSetup(await authApi.mfaEnrol()))
          }
        >
          Set up two-factor
        </Button>
      </>
    );
  }

  return (
    <Dialog
      open
      variant="modal"
      title="Two-factor"
      onClose={onClose}
      footer={footer}
    >
      {body}
    </Dialog>
  );
}
